import { getSpecialty } from '~/constants/specialties'
import { getSpecialtyContent } from '~/constants/specialtyContent'
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_THEME_NEUTRAL,
  DEFAULT_THEME_PRIMARY,
} from '~/composables/useBuilderMeta'
import type { ModuleKey, OnboardingAnswers } from '~/types/onboarding'
import { ALL_MODULE_KEYS, defaultModulesFor, normalizeModules } from '~/types/onboarding'
import type { LocalizedText, SiteSchema } from '~/types/site'
import { clinicTemplate } from './clinic'
import { deepClone } from './schema'
import { soloTemplate } from './solo'

export { clinicTemplate } from './clinic'
export { soloTemplate } from './solo'

/** Module → pages / block components that should be gated. */
export const MODULE_GATING: Record<
  ModuleKey,
  { pages?: string[]; components?: string[] }
> = {
  onlineBooking: { pages: ['booking'], components: ['BlockAppointment', 'BlockBookingWizard'] },
  healthRecord: { pages: [], components: [] },
  faq: { components: ['BlockFaq'] },
  blog: { pages: ['blog'], components: ['BlockBlogList'] },
  team: { pages: ['team'], components: ['BlockTeam'] },
  gallery: { components: ['BlockGallery'] },
  testimonials: { components: ['BlockTestimonials'] },
  insurance: { components: ['BlockInsurance'] },
  shop: { pages: ['shop'], components: ['BlockProducts'] },
  pricing: { pages: ['pricing'] },
  clinicCampaign: {
    components: ['BlockSpinWheel', 'BlockFlashSale', 'BlockCampaignBanner', 'BlockNewsletterCapture'],
  },
  servicePackages: { components: [] },
  leadGen: {
    components: [
      'BlockLeadMagnet',
      'BlockChecklist',
      'BlockLeadCalculator',
      'BlockLeadQuiz',
      'BlockConsultationForm',
      'BlockLeadSurvey',
      'BlockPersonalizedGuide',
      'BlockEventRegistration',
      'BlockWaitlistForm',
      'BlockSmartContactForm',
      'BlockLeadChatWidget',
    ],
  },
}

/** Components that must stay hidden in the builder when their module is off. */
export function blockedComponentsForModules(
  modules: Record<ModuleKey, boolean>,
): Set<string> {
  const blocked = new Set<string>()
  for (const key of ALL_MODULE_KEYS) {
    if (modules[key] === true) continue
    const gate = MODULE_GATING[key]
    for (const component of gate.components ?? []) {
      if (component === 'BlockProducts') continue
      blocked.add(component)
    }
  }
  if (!modules.shop && !modules.pricing) blocked.add('BlockProducts')
  return blocked
}

/** Page keys gated by modules that are currently off. */
export function blockedPagesForModules(
  modules: Record<ModuleKey, boolean>,
): Set<string> {
  const blocked = new Set<string>()
  for (const key of ALL_MODULE_KEYS) {
    if (modules[key] === true) continue
    for (const pageKey of MODULE_GATING[key].pages ?? []) blocked.add(pageKey)
  }
  return blocked
}

function setBlockEnabledEverywhere(
  schema: SiteSchema,
  component: string,
  enabled: boolean,
): void {
  for (const blockItem of schema.layout.blocks) {
    if (blockItem.component === component) blockItem.enabled = enabled
  }
  for (const pageItem of Object.values(schema.pages)) {
    for (const blockItem of pageItem.blocks) {
      if (blockItem.component === component) blockItem.enabled = enabled
    }
  }
}

function applyModuleGating(schema: SiteSchema, modules: Record<ModuleKey, boolean>): void {
  for (const key of ALL_MODULE_KEYS) {
    const enabled = modules[key] === true
    const gate = MODULE_GATING[key]
    if (gate.pages) {
      for (const pageKey of gate.pages) {
        const p = schema.pages[pageKey]
        if (p) p.enabled = enabled
      }
    }
    if (gate.components) {
      for (const component of gate.components) {
        if (component === 'BlockProducts') {
          if (key === 'shop') {
            const shopPage = schema.pages.shop
            if (shopPage) {
              for (const b of shopPage.blocks) {
                if (b.component === 'BlockProducts') b.enabled = enabled
              }
            }
            const homeProducts = schema.pages.home?.blocks.filter(
              (b) => b.component === 'BlockProducts',
            )
            if (homeProducts) {
              const anyCommerce = modules.shop || modules.pricing
              for (const b of homeProducts) b.enabled = anyCommerce
            }
          }
          continue
        }
        setBlockEnabledEverywhere(schema, component, enabled)
      }
    }
    if (key === 'pricing') {
      const pricingPage = schema.pages.pricing
      if (pricingPage) {
        for (const b of pricingPage.blocks) {
          if (b.component === 'BlockProducts') b.enabled = enabled
        }
      }
    }
  }
}

function applyBrand(schema: SiteSchema, answers: OnboardingAnswers): void {
  const brand: LocalizedText = {
    fa: answers.brandName.fa?.trim() || schema.title.fa || 'مطب من',
    en: answers.brandName.en?.trim() || schema.title.en || 'My Practice',
  }
  schema.title = { ...brand }

  const logo = answers.logoUrl?.trim() || ''

  const header = schema.layout.blocks.find((b) => b.component === 'BlockHeader')
  if (header) {
    header.props.logoText = { ...brand }
    header.props.logoImage = logo
  }
  const footer = schema.layout.blocks.find((b) => b.component === 'BlockFooter')
  if (footer) {
    footer.props.logoText = { ...brand }
    footer.props.logoImage = logo
    const phone = answers.phone.trim()
    footer.props.phone = {
      fa: phone ? `تلفن مطب: ${phone}` : 'تلفن مطب',
      en: phone ? `Clinic phone: ${phone}` : 'Clinic phone',
    }
    footer.props.address = {
      fa: answers.address.fa?.trim() || 'آدرس مطب',
      en: answers.address.en?.trim() || 'Clinic address',
    }
  }

  schema.siteSettings.logoUrl = logo
  schema.siteSettings.favicon = logo
  schema.siteSettings.themePrimary = answers.themePrimary || DEFAULT_THEME_PRIMARY
  schema.siteSettings.themeNeutral = answers.themeNeutral || DEFAULT_THEME_NEUTRAL
  schema.siteSettings.fontFamily = answers.fontFamily || DEFAULT_FONT_FAMILY

  const contactPage = schema.pages.contact
  if (contactPage) {
    for (const b of contactPage.blocks) {
      if (b.component !== 'BlockContact') continue
      const phone = answers.phone.trim()
      b.props.phone = { fa: phone || '—', en: phone || '—' }
      b.props.address = {
        fa: answers.address.fa?.trim() || 'آدرس مطب',
        en: answers.address.en?.trim() || 'Clinic address',
      }
    }
  }
}

function applySpecialty(schema: SiteSchema, answers: OnboardingAnswers): void {
  const specialty = getSpecialty(answers.specialty)
  const copy = getSpecialtyContent(answers.specialty)

  const serviceItems = copy.services.map((svc) => ({
    id: crypto.randomUUID(),
    title: { ...svc.title },
    description: { ...svc.description },
    price: '',
    icon: specialty.icon,
    image: '',
    features: svc.features.map((f) => ({ ...f })),
    ctaLabel: { en: 'Get consultation', fa: 'دریافت مشاوره' },
    ctaLink: '/booking',
  }))

  for (const pageItem of Object.values(schema.pages)) {
    for (const b of pageItem.blocks) {
      if (b.component === 'BlockServices') {
        b.props.items = serviceItems
        b.props.subtitle = {
          fa: `خدمات تخصصی در حوزه ${specialty.label.fa}`,
          en: `Specialized services in ${specialty.label.en}`,
        }
      }
      if (b.component === 'BlockSpecialties') {
        b.props.items = [
          {
            title: { ...specialty.label },
            items: copy.services.map((s) => ({ ...s.title })),
            ctaLink: '/services',
          },
        ]
      }
      if (b.component === 'BlockDoctorIntro') {
        b.props.description = {
          fa: `متخصص ${specialty.label.fa}`,
          en: `${specialty.label.en} specialist`,
        }
        b.props.intro = { ...copy.doctorIntro }
        if (answers.brandName.fa || answers.brandName.en) {
          b.props.title = {
            fa: answers.brandName.fa || answers.brandName.en || '',
            en: answers.brandName.en || answers.brandName.fa || '',
          }
        }
      }
      if (b.component === 'BlockLandingHero') {
        b.props.title = { ...copy.heroTitle }
        b.props.description = { ...copy.heroDescription }
        b.props.headlineImages = { en: [], fa: [] }
      }
      if (b.component === 'BlockFaq') {
        b.props.categories = [
          {
            title: { ...specialty.label },
            questions: copy.faq.map((q) => ({
              label: { ...q.label },
              content: { ...q.content },
            })),
          },
        ]
      }
      if (b.component === 'BlockTestimonials') {
        b.props.items = copy.testimonials.map((item) => ({
          name: { ...item.name },
          comment: { ...item.comment },
        }))
      }
    }
  }

  const banner = schema.layout.blocks.find((b) => b.component === 'BlockBanner')
  if (banner) {
    banner.props.title = { ...copy.bannerTitle }
  }
}

function stripLocaleDeep(value: unknown, locale: string): void {
  if (!value || typeof value !== 'object') return
  if (Array.isArray(value)) {
    value.forEach((item) => stripLocaleDeep(item, locale))
    return
  }
  const record = value as Record<string, unknown>
  const keys = Object.keys(record)
  const isStringMap =
    keys.length > 0 && keys.every((k) => typeof record[k] === 'string' || record[k] === undefined)
  if (isStringMap && (locale in record)) {
    delete record[locale]
    return
  }
  Object.values(record).forEach((v) => stripLocaleDeep(v, locale))
}

function applyI18n(schema: SiteSchema, answers: OnboardingAnswers): void {
  schema.i18n.enabled = answers.i18nEnabled
  if (answers.i18nEnabled) {
    schema.i18n.locales = ['fa', 'en']
    schema.i18n.defaultLocale = 'fa'
  } else {
    schema.i18n.locales = ['fa']
    schema.i18n.defaultLocale = 'fa'
    stripLocaleDeep(schema, 'en')
  }
}

function syncMenuToEnabledPages(schema: SiteSchema): void {
  const header = schema.layout.blocks.find((b) => b.component === 'BlockHeader')
  if (!header) return
  const menuItems = header.props.menuItems
  if (!Array.isArray(menuItems)) return

  const enabledSlugs = new Set(
    Object.values(schema.pages)
      .filter((p) => p.enabled)
      .map((p) => p.slug),
  )
  enabledSlugs.add('/')

  header.props.menuItems = menuItems.filter((item) => {
    if (!item || typeof item !== 'object') return false
    const to = (item as { to?: string }).to
    if (!to || to.startsWith('http') || to.startsWith('#')) return true
    return enabledSlugs.has(to)
  })
}

/**
 * Build a complete SiteSchema from onboarding answers.
 * Deep-clones the matching template, then applies brand / specialty / modules / theme / i18n.
 */
export function buildSchemaFromOnboarding(answers: OnboardingAnswers): SiteSchema {
  const base = answers.businessType === 'clinic' ? clinicTemplate : soloTemplate
  const schema = deepClone(base)

  const modules = normalizeModules(
    {
      ...defaultModulesFor(answers.businessType),
      ...answers.modules,
    },
    answers.businessType,
  )

  applyBrand(schema, answers)
  applySpecialty(schema, answers)
  applyModuleGating(schema, modules)
  applyI18n(schema, answers)
  syncMenuToEnabledPages(schema)

  if (answers.businessType === 'solo') {
    for (const b of schema.pages.about?.blocks ?? []) {
      if (b.component === 'BlockDoctorIntro') b.enabled = true
    }
  }

  if (answers.businessType === 'clinic') {
    for (const b of schema.pages.about?.blocks ?? []) {
      if (b.component === 'BlockDoctorIntro') b.enabled = false
    }
  }

  return schema
}
